import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Camera, Crosshair, MapPin } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { AngaMark } from "@/components/brand/AngaMark";
import { Button } from "@/components/ui/button";
import { ApiError, reportsApi } from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";
import { reverseGeocodeMapbox, searchPlacesMapbox, type MapboxSearchHit } from "@/lib/geo";

export const Route = createFileRoute("/app/report")({
  head: () => ({
    meta: [
      { title: "Report a hazard" },
      {
        name: "description",
        content: "Report flooding or an impassable road near you.",
      },
    ],
  }),
  component: SelfReportPage,
});

function SelfReportPage() {
  const navigate = useNavigate();
  const { platformName, config } = usePlatformConfig();
  const mapToken = config?.mapbox?.accessToken || "";
  const fileRef = useRef<HTMLInputElement>(null);

  const [description, setDescription] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [county, setCounty] = useState("");
  const [ward, setWard] = useState("");
  const [placeLabel, setPlaceLabel] = useState("");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<MapboxSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [fetchingGps, setFetchingGps] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function applyCoords(lat: number, lng: number) {
    setLatitude(lat);
    setLongitude(lng);
    if (!mapToken) {
      setPlaceLabel(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      return;
    }
    const geo = await reverseGeocodeMapbox(lat, lng, mapToken);
    if (geo) {
      setCounty(geo.county);
      setWard(geo.ward);
      setPlaceLabel(geo.placeName || `${geo.ward}, ${geo.county}`);
    } else {
      setPlaceLabel(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    }
  }

  function fetchLocation() {
    if (!navigator.geolocation) {
      toast.error("GPS is not available on this device");
      return;
    }
    setFetchingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void applyCoords(pos.coords.latitude, pos.coords.longitude).finally(() =>
          setFetchingGps(false),
        );
      },
      () => {
        setFetchingGps(false);
        toast.error("Could not read your GPS location");
      },
      { enableHighAccuracy: true, timeout: 20_000 },
    );
  }

  async function onSearch() {
    const q = query.trim();
    if (q.length < 2) return;
    if (!mapToken) {
      toast.error("Map search is not configured");
      return;
    }
    setSearching(true);
    try {
      const results = await searchPlacesMapbox(q, mapToken, 6);
      setHits(results);
      if (!results.length) toast.message("No places found");
    } catch {
      toast.error("Place search failed");
    } finally {
      setSearching(false);
    }
  }

  function onPickHit(hit: MapboxSearchHit) {
    setHits([]);
    setQuery(hit.placeName);
    setLatitude(hit.latitude);
    setLongitude(hit.longitude);
    setCounty(hit.county || "");
    setWard(hit.ward || "");
    setPlaceLabel(hit.placeName);
  }

  function onPickImage(file: File | null) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose a photo (JPEG or PNG)");
      return;
    }
    if (file.size > 2.5 * 1024 * 1024) {
      toast.error("Photo must be under 2.5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : null;
      setImageDataUrl(result);
    };
    reader.readAsDataURL(file);
  }

  async function submit() {
    if (!description.trim() || description.trim().length < 8) {
      toast.error("Describe the issue in a bit more detail");
      return;
    }
    if (latitude == null || longitude == null) {
      toast.error("Set a location first (search or fetch GPS)");
      return;
    }
    setSubmitting(true);
    try {
      const res = await reportsApi.submit({
        description: description.trim(),
        latitude,
        longitude,
        county: county || null,
        ward: ward || null,
        placeLabel: placeLabel || null,
        imageDataUrl,
      });
      if (res.code === "ALERTED") {
        toast.success(res.message || "Report sent — nearby users are being notified");
        void navigate({ to: "/app/" });
        return;
      }
      if (res.code === "RECORDED_NO_ALERT") {
        toast.message(res.message || "Report recorded");
        void navigate({ to: "/app/" });
        return;
      }
      toast.success(res.message || "Report submitted");
      void navigate({ to: "/app/" });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "REJECTED_NUDITY") {
          toast.error(
            err.message ||
              "That photo was flagged as inappropriate. The report was discarded.",
          );
          setImageDataUrl(null);
          if (fileRef.current) fileRef.current.value = "";
          return;
        }
        toast.error(err.message || "Could not submit report");
        return;
      }
      toast.error("Could not submit report");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary-soft),_transparent_55%)]" />
      <div className="relative mx-auto flex min-h-screen max-w-lg flex-col px-5 py-6 pb-16">
        <header className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#064E3B] text-white">
            <AngaMark className="h-6 w-6" title={platformName} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-lg font-bold leading-none">{platformName}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              Self report
            </p>
          </div>
          <Button asChild size="sm" variant="ghost">
            <Link to="/app/">Back</Link>
          </Button>
        </header>

        <section className="space-y-5">
          <div>
            <h1 className="font-display text-2xl font-bold">Report a hazard</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Flooding, impassable roads, or other local risks. Photos are checked for
              inappropriate content before anyone is notified.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Location
            </p>
            <div className="mt-3 flex gap-2">
              <input
                className="h-10 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                placeholder="Search a place in Kenya"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void onSearch();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                disabled={searching}
                onClick={() => void onSearch()}
              >
                {searching ? "…" : "Search"}
              </Button>
            </div>
            {hits.length ? (
              <ul className="mt-2 max-h-40 overflow-auto rounded-md border border-border bg-background">
                {hits.map((hit) => (
                  <li key={hit.id}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-muted/50"
                      onClick={() => onPickHit(hit)}
                    >
                      {hit.placeName}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <Button
              type="button"
              className="mt-3 w-full"
              variant="secondary"
              disabled={fetchingGps}
              onClick={fetchLocation}
            >
              <Crosshair className="mr-2 h-4 w-4" />
              {fetchingGps ? "Fetching location…" : "Fetch my location"}
            </Button>
            {latitude != null && longitude != null ? (
              <p className="mt-3 inline-flex items-start gap-2 text-sm">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  <span className="font-semibold">{placeLabel || "Selected location"}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {[ward, county].filter(Boolean).join(", ")}
                    {ward || county ? " · " : ""}
                    {latitude.toFixed(5)}, {longitude.toFixed(5)}
                  </span>
                </span>
              </p>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                Search a place or use GPS so we can notify the right people.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Describe the issue
              <textarea
                className="mt-2 min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="e.g. Thika Road near Juja is flooded and cars cannot pass."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={2000}
              />
            </label>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Photo (optional)
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Hazard scenes only. Nudity or sexual content is blocked and the report is discarded.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="mt-3 block w-full text-sm"
              onChange={(e) => onPickImage(e.target.files?.[0] || null)}
            />
            {imageDataUrl ? (
              <div className="mt-3 overflow-hidden rounded-md border border-border">
                <img src={imageDataUrl} alt="Report preview" className="max-h-56 w-full object-cover" />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setImageDataUrl(null);
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                >
                  Remove photo
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                className="mt-3 w-full"
                variant="secondary"
                onClick={() => fileRef.current?.click()}
              >
                <Camera className="mr-2 h-4 w-4" />
                Take or choose photo
              </Button>
            )}
          </div>

          <Button className="w-full" disabled={submitting} onClick={() => void submit()}>
            {submitting ? "Checking & sending…" : "Submit report"}
          </Button>
        </section>
      </div>
    </div>
  );
}
